<script setup lang="ts">
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import RoundedBox from "@/components/RoundedBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledInput from "@/components/StyledInput.vue";
import StyledSelect from "@/components/StyledSelect.vue";
import StyledSwitch from "@/components/StyledSwitch.vue";
import useCredit from "@/composables/useCredit";
import FieldSet from "@/modules/companies/components/FieldSet.vue";
import { Hotel } from "@/modules/hotels/interfaces/hotel";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { ref, watch } from "vue";
import { useToast } from "vue-toastification";
import useHotelCredit from "../composables/useHotelCredit";

interface Props {
  hotel: Hotel;
}

const props = defineProps<Props>();

const { data, isLoading, refetch, createMutationAsync, createMutationLoading } =
  useHotelCredit({
    params: {
      id: props.hotel.id,
    },
    immediate: true,
    config: {
      errorHandling: false,
    },
  });

const enabled = ref<boolean>(Boolean(data.value));

const {
  updateCompanyCreditAsync,
  deleteCompanyCreditAsync,
  restoreCompanyCreditAsync,
  updateCompanyCreditLoading,
} = useCredit({
  params: {
    creditId: 0,
  },
  immediate: false,
});

const toast = useToast();

const creditFields = ref({
  legalRepresentativeName: "",
  legalRepresentativeEmail: "",
  limit: "",
});

const fieldInfo = {
  legalRepresentativeName: ["Representante legal:", "ej. Juan Pérez"],
  legalRepresentativeEmail: [
    "Correo electrónico:",
    "ej. manuel.mtzv816@gmail.com",
  ],
  limit: ["Límite de crédito:", "ej. 100,000"],
};

const termsOfCredit = [
  { label: "5 días", value: 5 },
  { label: "10 días", value: 10 },
  { label: "15 días", value: 15 },
  { label: "30 días", value: 30 },
  { label: "45 días", value: 45 },
];

const creditSelects = ref({
  deadline: "",
});

type creditFieldsKey = keyof typeof creditFields.value;
type fieldInfoKey = keyof typeof fieldInfo;

const rules = {
  legalRepresentativeName: {
    required: helpers.withMessage(
      "El nombre de representante legal es obligatorio",
      required,
    ),
  },
  legalRepresentativeEmail: {
    required: helpers.withMessage(
      "El correo electrónico del representante legal es obligatorio",
      required,
    ),
    requiredIf: helpers.withMessage(
      "El correo electrónico no es válido",
      (value: any) => {
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
        return emailRegex.test(value);
      },
    ),
  },
  limit: {
    required: helpers.withMessage(
      "El límite de crédito es obligatorio",
      required,
    ),
  },
};

const v$ = useVuelidate(rules, creditFields);

const handleCreate = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  await createMutationAsync({
    legalRepresentativeName: creditFields.value.legalRepresentativeName,
    legalRepresentativeEmail: creditFields.value.legalRepresentativeEmail,
    deadline: creditSelects.value.deadline,
    limit: creditFields.value.limit,
    id: props.hotel.id,
  });

  await refetch();
};

const handleUpdate = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  await updateCompanyCreditAsync({
    params: {
      creditId: data.value?.id,
      legalRepresentativeName: creditFields.value.legalRepresentativeName,
      legalRepresentativeEmail: creditFields.value.legalRepresentativeEmail,
      deadline: Number(creditSelects.value.deadline),
      limit: creditFields.value.limit,
    },
    config: {
      raiseToast: true,
      onSuccessToast: "Crédito actualizado correctamente",
      onErrorToast: "Error al actualizar el crédito",
    },
  });

  refetch();
};

const handleRestoreCredit = async () => {
  await restoreCompanyCreditAsync({
    params: {
      id: data.value?.id!,
    },
    config: {
      raiseToast: true,
      onSuccessToast: "Crédito restaurado correctamente",
      onErrorToast: "Error al restaurar el crédito",
    },
  });

  refetch();
};

const handledDeleteCredit = async () => {
  await deleteCompanyCreditAsync({
    params: {
      id: data.value?.id!,
    },
    config: {
      raiseToast: true,
      onSuccessToast: "Crédito eliminado correctamente",
      onErrorToast: "Error al eliminar el crédito",
    },
  });

  refetch();
};

watch(
  () => data.value,
  (value) => {
    if (value) {
      enabled.value = !value.deleted;

      creditFields.value.legalRepresentativeName =
        value.legalRepresentativeName;
      creditFields.value.legalRepresentativeEmail =
        value.legalRepresentativeEmail;
      creditFields.value.limit = value.limit;
      creditSelects.value.deadline = value.deadline.toString();
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <RoundedBox class="bg-white">
    <section class="w-full grid gap-[1.563rem]" v-if="!isLoading">
      <div class="flex items-center justify-between gap-4">
        <h2 class="text-xl font-medium">Crédito</h2>

        <div>
          <HasPermission
            :entity-type="'App\\Models\\Hotel'"
            :name-permission="'create-credit-internal'"
          >
            <StyledButton
              label="Nuevo crédito"
              class="bg-[#253C70] text-white ml-auto"
              @click="enabled = !enabled"
              v-if="!data"
            />
          </HasPermission>

          <div v-if="data">
            <HasPermission
              :entity-type="'App\\Models\\Hotel'"
              :name-permission="'restore-credit-internal'"
            >
              <StyledSwitch
                v-if="data.deleted"
                v-model:enabled="enabled"
                @update:enabled="handleRestoreCredit"
              />
            </HasPermission>

            <HasPermission
              :entity-type="'App\\Models\\Hotel'"
              :name-permission="'delete-credit-internal'"
            >
              <StyledSwitch
                v-if="!data.deleted"
                v-model:enabled="enabled"
                @update:enabled="handledDeleteCredit"
              />
            </HasPermission>
          </div>
        </div>
      </div>

      <template v-if="enabled">
        <FieldSet>
          <StyledInput
            :is-required="true"
            v-for="key in Object.keys(creditFields)"
            :key="key"
            :label="fieldInfo[key as fieldInfoKey].at(0)"
            :placeholder="fieldInfo[key as fieldInfoKey].at(1)"
            v-model="creditFields[key as creditFieldsKey]"
          />

          <StyledSelect
            :is-required="true"
            label="Plazo de crédito"
            placeholder="Seleccione un plazo"
            :options="termsOfCredit"
            v-model="creditSelects.deadline"
          />
        </FieldSet>

        <div>
          <HasPermission
            :entity-type="'App\\Models\\Hotel'"
            :name-permission="'create-credit-internal'"
          >
            <StyledButton
              class="bg-[#253C70] text-white ml-auto"
              label="Guardar"
              v-if="!data"
              @click.prevent="handleCreate()"
              :loading="createMutationLoading"
            />
          </HasPermission>

          <HasPermission
            :entity-type="'App\\Models\\Hotel'"
            :name-permission="'update-credit-internal'"
          >
            <StyledButton
              class="bg-[#253C70] text-white ml-auto"
              label="Guardar cambios"
              v-if="data"
              @click.prevent="handleUpdate()"
              :loading="updateCompanyCreditLoading"
          /></HasPermission>
        </div>
      </template>
    </section>
  </RoundedBox>
  <LoadingModal :when="isLoading" />
</template>

<style scoped></style>
